import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {Sequence} from "../../../../core/displayobject/Sequence";
import {PREngineManager} from "../../../punkEngine/PREngineManager";
import {PunkRocker} from "../../../PunkRocker";
import {BLEND_MODES} from "pixi.js";
import {gsap} from "gsap";
import {Helper} from "../../../../core/utils/Helper";
import {TrainState} from "./TrainState";
import {SoundManager} from "../../../sound/SoundManager";
import {SoundConfig} from "../../../sound/SoundConfig";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {BaseServerData} from "../../../server/base/IData";
import {PRGameMode} from "../../../server/PRGameMode";
import {GameData} from "../../../server/data/GameData";
import Skin = PIXI.spine.core.Skin;

export class TrainView extends PIXI.Container implements StageLayer {
    protected _trainSpine: PIXI.spine.Spine;
    protected _trainSpineSkins: string[] = [];
    protected _trainSpineData: PIXI.spine.core.SkeletonData;
    protected _trainShowItems: PIXI.Sprite[] = [];
    protected _trainItemGlow: Sequence[] = [];
    protected attentionSeq: Sequence;
    protected trainType: string = "";
    protected _animationLoop: gsap.core.Timeline;
    protected _timeLine: gsap.core.Timeline;
    protected _trainMask: PIXI.Graphics;

    constructor(trainType: string, showTrain: boolean) {
        super();

        this.trainType = trainType;
        this._trainSpineData = PunkRocker.assetLoader.getSpineAsset(this.trainType + "Spine");
        this.init(showTrain);
    }

    private _currState: TrainState;

    get currState(): TrainState {
        return this._currState;
    }

    set currState(value: TrainState) {
        this._currState = value;
    }

    public isTrainVisible(whichTrain: string): boolean {
        let flag: boolean = false;

        if (whichTrain === this.trainType && this.visible) {
            flag = true;
        }

        return flag;
    }

    public toggleDisplay(showTrain: boolean): void {
        this.visible = showTrain; //this is for whole train visibility
    }

    public getAnimDurr(state: string): number {
        return ((this._trainSpine.skeleton.data.findAnimation(state).duration) / this._trainSpine.state.timeScale);
    }

    public setTrainState(state: string, isFromFeature: boolean = false): gsap.core.Timeline {
        const gameData: GameData = PunkRocker.model.gameData,
            rawGameData: BaseServerData = gameData.raw,
            currMode: PRGameMode = gameData.mode,
            nextMode: PRGameMode = gameData.nextMode,
            isUpgrade: boolean = ((currMode === PRGameMode.FREESPIN_LEVEL_ONE && (nextMode === PRGameMode.FREESPIN_LEVEL_TWO || nextMode === PRGameMode.FREESPIN_LEVEL_THREE))
                || (currMode === PRGameMode.FREESPIN_LEVEL_TWO && nextMode === PRGameMode.FREESPIN_LEVEL_THREE));

        if (!(gameData.gameModeChanges) || rawGameData.freespinsLeft === 0 || isUpgrade) {
            let trainValuesForSkin: string[] = ['BLANK', 'BLANK', 'BLANK', 'BLANK', 'BLANK', 'BLANK'],
                burningStatus: boolean[] = [false, false, false];

            if (state === TrainState.TRAIN_ENTER || state === TrainState.TRAIN_LOOP) {
                if (this.trainType === "bottomTrain" && PunkRocker.model.gameData.raw.mode === PRGameMode.FREESPIN_LEVEL_THREE) {
                    burningStatus = [false, true, false];
                }
            } else {
                trainValuesForSkin = (this.trainType === "topTrain") ? rawGameData.topTrain : rawGameData.bottomTrain;
                burningStatus = rawGameData.burningTrain;
            }

            this.setTrainCartSkins(trainValuesForSkin, burningStatus);
        }

        if (!isFromFeature && this.trainType === "bottomTrain" && (rawGameData.mode === PRGameMode.NORMAL && (rawGameData.nextMode === PRGameMode.FREESPIN_LEVEL_TWO || rawGameData.nextMode === PRGameMode.FREESPIN_LEVEL_THREE))) {
            isFromFeature = true;
        }
        if (!isFromFeature) {
            if (!(PunkRocker.model.gameData.raw.bottomTrainEnabled) && this.trainType === "bottomTrain") {
                return Helper.setTimeline();
            }
        }
        if (state === TrainState.TRAIN_STOP_OPEN) {
            this._trainSpine.stateData.setMix(TrainState.TRAIN_LOOP, TrainState.TRAIN_STOP_OPEN, 0.05);
        }
        if (state === TrainState.TRAIN_OPEN_ONLY && this.trainType === "bottomTrain") {
            this._trainSpine.stateData.setMix(TrainState.TRAIN_LOOP, TrainState.TRAIN_OPEN_ONLY, 0.05);
        }

        if (state === TrainState.TRAIN_ENTER) {
            if (this.trainType === "topTrain") {
                SoundManager.playSoundEffect(SoundConfig.TRAIN_MOVES, 1000000);
                SoundManager.onFadeSoundEffect(SoundConfig.TRAIN_MOVES, 1, MathHelper.frameToSeconds(10));
            }
        } else if (state === TrainState.TRAIN_CLOSE_EXIT) {
            if (this.trainType === "topTrain") {
                SoundManager.fadeAmbienceLoop(1, 2200);
                SoundManager.playSoundEffect(SoundConfig.TRAIN_OPEN_CLOSE);
            }
            const tl = gsap.timeline();
            tl.to(this, {
                duration: this.getAnimDuration(state)
            });

            tl.add(() => {
                SoundManager.onFadeSoundEffect(SoundConfig.TRAIN_MOVES, 0, 1000);
            });
            tl.to(this, {duration: MathHelper.frameToSeconds(10)});
        } else if (state === TrainState.TRAIN_STOP_OPEN) {
            let delay: number = MathHelper.frameToSeconds(36);
            if (TrainState.TOP_CART_MODE == "") {
                delay = MathHelper.frameToSeconds(26);
            }
            gsap.to(this, {
                duration: delay
            }).eventCallback("onComplete", () => {
                if ((this.trainType === "topTrain" && !(PunkRocker.trainsManager.bottomTrain.visible))
                    || (this.trainType === "bottomTrain" && PunkRocker.trainsManager.bottomTrain.visible)
                ) {
                    SoundManager.stopSoundEffect(SoundConfig.TRAIN_MOVES);
                    SoundManager.fadeAmbienceLoop(0.85, 1400);
                    SoundManager.playSoundEffect(SoundConfig.TRAIN_OPEN_CLOSE);
                }
            });
        } else if (state === TrainState.TRAIN_OPEN_ONLY) {
            gsap.to(this, {
                duration: 0.001
            }).eventCallback("onComplete", () => {
                if ((this.trainType === "topTrain" && !(PunkRocker.trainsManager.bottomTrain.visible))
                    || (this.trainType === "bottomTrain" && PunkRocker.trainsManager.bottomTrain.visible)
                ) {
                    SoundManager.stopSoundEffect(SoundConfig.TRAIN_MOVES);
                    SoundManager.fadeAmbienceLoop(0.85, 1400);
                    SoundManager.playSoundEffect(SoundConfig.TRAIN_OPEN_CLOSE);
                }
            });
        }

        this.currState = state;

        const spineAnimName: string = TrainState.getAnimName(state);

        if (this.trainType === "bottomTrain" && (!(gameData.gameModeChanges) || rawGameData.freespinsLeft === 0)) {
            if (rawGameData.mode === PRGameMode.FREESPIN_LEVEL_THREE) {
                this._trainSpineSkins[this._trainSpineSkins.indexOf("5_plain")] = "5_plain_burnt";
            }
        }

        return this.playSpine(spineAnimName, this._trainSpineSkins, ((state === TrainState.TRAIN_LOOP) || (state === TrainState.TRAIN_OPEN_LOOP)));
    }

    public performTrainBlast(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();

        if (this.trainType === "bottomTrain") {
            const firstFeaturedCartIndex: number = this._trainSpineSkins.indexOf("1_feature_noGuns");

            if (firstFeaturedCartIndex > -1) {
                this._trainSpineSkins[firstFeaturedCartIndex] = "1_feature_burnt";
            }

            tl.add(this.playSpine(TrainState.TRAIN_OPEN_LOOP, this._trainSpineSkins, true));
        }

        return tl;
    }

    public trainLoop(active: boolean, isFromFeature: boolean = false): void {
        if (!isFromFeature) {
            if (!(PunkRocker.model.gameData.raw.bottomTrainEnabled) && this.trainType === "bottomTrain") {
                return;
            }
        }
        this._animationLoop = Helper.setTimeline(this._animationLoop);
        if (active) {
            this._animationLoop = new gsap.core.Timeline({
                onComplete: () => {
                    this.trainLoop(true);
                }
            });
            this._animationLoop.add(this.playSpine(TrainState.TRAIN_LOOP, this._trainSpineSkins, true), 0);
            //this._trainSpine.state.timeScale = 1.75;

            this.currState = TrainState.TRAIN_LOOP;
        }
    }

    public setTrainItems(trainValues: string[]): void {
        const gameData: GameData = PunkRocker.model.gameData;

        if (gameData.gameModeChanges) {
            this.setTrainCartSkins(trainValues, gameData.raw.burningTrain);
        }

        const textures: PIXI.Texture[] = TrainState.getIcons(this.trainType, trainValues);
        for (let i: number = 0; i < this._trainShowItems.length; i++) {
            (this._trainShowItems[i] as PIXI.Sprite).texture = textures[i];
        }
    }

    public setTrainAlpha(show: boolean, durr: number = 0.3): gsap.core.Timeline {
        this._timeLine = Helper.setTimeline(this._timeLine, new gsap.core.Timeline());

        this._timeLine.to(this._trainSpine, {
            duration: durr,
            alpha: show ? 1 : 0
        }, 0);

        return this._timeLine;
    }

    public showGlowPositions(pos: number[], active: boolean, duration: number = 0.2): gsap.core.Timeline[] {
        const tlArr: gsap.core.Timeline[] = [];

        for (let p: number = 0; p < pos.length; p++) {
            const seq: Sequence = this._trainItemGlow[pos[p]];
            seq.visible = true;
            seq.reset(false);
            if (active) {
                seq.timeline!.fromTo(seq, {alpha: active ? 0 : 1}, {duration: duration, alpha: active ? 1 : 0}, 0);
            } else {
                const fromA: number = seq.alpha;
                seq.timeline!.fromTo(seq, {alpha: fromA}, {duration: duration, alpha: 0}, 0);
            }
            tlArr.push(seq.timeline!);
        }
        return tlArr;
    }

    public playAttention(index: number, duration?: number): gsap.core.Timeline {
        this.attentionSeq.position = this._trainItemGlow[index].position;
        this.attentionSeq.visible = true;
        return this.attentionSeq.play({
            onCompleteCallback: () => {
                this.attentionSeq.alpha = 0;
                this.attentionSeq.visible = false;
            },
            onStart: () => {
                this.attentionSeq.alpha = 1;
            },
            duration: duration
        });
    }

    public timeLeftInLoop(): number {
        if (this._animationLoop) {
            return (this._animationLoop.duration() - this._animationLoop.time()) / this._animationLoop.timeScale();
        }
        return 0;
    }

    public showTrainItems(val: boolean, hidePos: number[], duration: number = 0.3): gsap.core.Timeline {
        this._timeLine = Helper.setTimeline(this._timeLine, new gsap.core.Timeline())

        for (let i: number = 0; i < this._trainShowItems.length; i++) {
            if (val) {
                const fromA: number = hidePos.indexOf(i) >= 0 ? (val ? 0 : 1) : (val ? 0 : 0.6);
                const fromT: number = hidePos.indexOf(i) >= 0 ? (val ? 1 : 0) : (val ? 0.6 : 0);
                this._timeLine.fromTo(this._trainShowItems[i], {alpha: fromA}, {alpha: fromT, duration: duration}, 0);
            } else {
                const fromA: number = this._trainShowItems[i].alpha;
                const fromT: number = 0;
                this._timeLine.fromTo(this._trainShowItems[i], {alpha: fromA}, {alpha: fromT, duration: duration}, 0);
            }
        }
        return this._timeLine;
    }

    public getAnimDuration(animName: string): number {
        return this._trainSpine.skeleton.data.findAnimation(animName).duration;
    }

    private setTrainCartSkins(trainValues: string[], bottomTrainBurningStatus: boolean[]): void {
        const cart_plain: string = "_plain",
            cartFeatured: string = "_feature";

        this._trainSpineSkins = [];
        if (this.trainType === "topTrain") {
            let topCartLeft: string = "2",
                topCartRight: string = "1";

            const topCart_a: string = "_sheesh",
                topCart_b: string = "_anarchy";

            if (trainValues[0] !== "BLANK" || trainValues[1] !== "BLANK" || trainValues[2] !== "BLANK") {
                if (trainValues[3] !== "BLANK" || trainValues[4] !== "BLANK" || trainValues[5] !== "BLANK") {
                    topCartLeft += cartFeatured + topCart_b;
                } else {
                    topCartLeft += cartFeatured + topCart_a;
                }
            } else {
                topCartLeft += cart_plain;
            }

            if (trainValues[3] !== "BLANK" || trainValues[4] !== "BLANK" || trainValues[5] !== "BLANK") {
                topCartRight += cartFeatured + topCart_a;
            } else {
                topCartRight += cart_plain;
            }

            this._trainSpineSkins.push("4_plain", "3_plain", topCartLeft, topCartRight);
        } else {
            let bottomCartLeft: string = "3",
                bottomCartMiddle: string = "2",
                bottomCartRight: string = "1";

            const cartBurning: string = "_burnt",
                bottomCart_a: string = "_noGuns",
                bottomCart_b: string = "_oinkOink",
                bottomCart_c: string = "_freedom";

            if (trainValues[0] !== "BLANK" || trainValues[1] !== "BLANK") {
                if ((trainValues[4] !== "BLANK" || trainValues[5] !== "BLANK") && (trainValues[2] !== "BLANK" || trainValues[3] !== "BLANK")) {
                    bottomCartLeft += cartFeatured + (bottomTrainBurningStatus[0] ? cartBurning : bottomCart_c);
                } else if ((trainValues[4] !== "BLANK" || trainValues[5] !== "BLANK") && (trainValues[2] === "BLANK" && trainValues[3] === "BLANK") || (trainValues[4] === "BLANK" && trainValues[5] === "BLANK") && (trainValues[2] !== "BLANK" || trainValues[3] !== "BLANK")) {
                    bottomCartLeft += cartFeatured + (bottomTrainBurningStatus[0] ? cartBurning : bottomCart_b);
                } else {
                    bottomCartLeft += cartFeatured + (bottomTrainBurningStatus[0] ? cartBurning : bottomCart_a);
                }
            } else {
                bottomCartLeft += cart_plain + (bottomTrainBurningStatus[0] ? cartBurning : "");
            }

            if (trainValues[2] !== "BLANK" || trainValues[3] !== "BLANK") {
                if (trainValues[4] !== "BLANK" || trainValues[5] !== "BLANK") {
                    bottomCartMiddle += cartFeatured + (bottomTrainBurningStatus[1] ? cartBurning : bottomCart_b);
                } else {
                    bottomCartMiddle += cartFeatured + (bottomTrainBurningStatus[1] ? cartBurning : bottomCart_a);
                }
            } else {
                bottomCartMiddle += cart_plain + (bottomTrainBurningStatus[1] ? cartBurning : "");
            }

            if (trainValues[4] !== "BLANK" || trainValues[5] !== "BLANK") {
                bottomCartRight += cartFeatured + (bottomTrainBurningStatus[2] ? cartBurning : bottomCart_a);
            } else {
                bottomCartRight += cart_plain + (bottomTrainBurningStatus[2] ? cartBurning : "");
            }

            this._trainSpineSkins.push("6_plain", "5_plain", "4_plain", bottomCartLeft, bottomCartMiddle, bottomCartRight);
        }
    }

    private init(showTrain: boolean): void {
        this._trainSpine = new PIXI.spine.Spine(this._trainSpineData);
        this.createTrainMask();
        this._trainSpine.name = this.trainType;
        this.addChild(this._trainSpine);

        this._trainShowItems = this.createTrainItems();

        this._trainItemGlow = this.createTrainItems("Train_Attention_Sector");
        this._trainItemGlow.forEach(seq => {
            seq.blendMode = BLEND_MODES.ADD;
            seq.visible = false;
        });

        this.attentionSeq = Sequence.createSequence(PunkRocker.assetLoader.getTextures("Attention_sector"));
        this.attentionSeq.blendMode = BLEND_MODES.ADD;
        this.attentionSeq.visible = false;
        this.attentionSeq.name = "attentionSeq";
        this.addChild(this.attentionSeq);

        this.toggleDisplay(showTrain);
    }

    private createTrainItems(path: string = ""): Sequence[] {
        let offsetX: number = -290,
            resSeqArr: Sequence[] = [];

        for (let reel: number = 0; reel < PREngineManager.AllReels.length; reel++) {
            const seq: Sequence = new Sequence({
                textures: PunkRocker.assetLoader.getTextures(path)
            });
            seq.alpha = 0;
            seq.position.set(offsetX, 0)
            seq.anchor.set(0.5, 0.5);
            this.addChild(seq);
            resSeqArr.push(seq);
            offsetX += 116;
        }
        return resSeqArr;
    }

    private resetSpine(showSpine: boolean = false): void {
        this._trainSpine.state.clearTracks();
        this._trainSpine.skeleton.setToSetupPose();
        this._trainSpine.skeleton.setBonesToSetupPose();
        this._trainSpine.skeleton.setSlotsToSetupPose();
        this._trainSpine.state.setEmptyAnimation(0, 0);
        this._trainSpine.visible = showSpine;
    }

    private playSpine(animName: string, skinsArr: string[] = ["default"], isLooped: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            animDur: number = this.getAnimDuration(animName),
            combinedSkin: Skin = new PIXI.spine.core.Skin("combinedSkin");

        for (let i: number = 0; i < skinsArr.length; i++) {
            combinedSkin.addSkin(this._trainSpineData.findSkin(skinsArr[i]));
        }

        tl.to(this, {
            duration: Math.max(0.033, animDur),
            onStart: () => {
                this.resetSpine(true);
                this._trainSpine.skeleton.setSkin(combinedSkin);
                this._trainSpine.state.setAnimation(0, animName, isLooped);
                this._trainSpine.skeleton.setSlotsToSetupPose();
            }
        });

        return tl;
    }

    private createTrainMask(): void {
        this._trainMask = new PIXI.Graphics();
        this._trainMask.beginFill(0xFFFF00, 1);

        const maskHeight: number = (this.trainType === "topTrain") ? 64 : 150,
            maskPosY: number = (this.trainType === "topTrain") ? -33 : -122;

        this._trainMask.drawShape(new PIXI.Rectangle(0, 0, 692, maskHeight));
        this._trainMask.endFill();
        this._trainMask.name = "trainMask";
        this._trainMask.position.set(-346, maskPosY);
        this._trainSpine.mask = this._trainMask;
        this.addChild(this._trainMask);
    }
}